import { AfterViewInit, Component, computed, DestroyRef, effect, ElementRef, HostListener, inject, input, NgZone, output, signal, TemplateRef, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkDragDrop, CdkDragEnd, CdkDragMove, CdkDragSortEvent, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpParams } from '@angular/common/http';
import { EMPTY, firstValueFrom, from, Observable } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { ButtonActionComponent } from 'src/app/features/shared/controls/container/button-action/button-action.component';
import { CLAIM_PAGE } from 'src/app/infrastructure/injection-tokens/PAGE-NAME';
import { CameraCaptureComponent } from '../camera-capture/camera-capture.component';
import { ConfigService } from 'src/app/infrastructure/services/config-services/config.service';
import { FileResizeService } from 'src/app/infrastructure/services/file-resize/file-resize.service';
import { FilterPipe } from 'src/app/infrastructure/pipes/filter.pipe';
import { FilterService } from 'src/app/features/shared/filter/filter.service';
import { ImageListService } from '../../services/image-list.service';
import { IImageListData, imageListData } from '../../models/image-list-data';
import { IImageListDto } from 'src/assets/common/models/image-list-dto.g';
import { ImageBoxTabListComponent } from 'src/app/features/shared/image-boxes/image-box-tab-list/image-box-tab-list.component';
import { LIST_KIND_NAME, _ListService } from 'src/app/infrastructure/services/list-services/_list.service';
import { NotificationService } from 'src/app/features/shared/notification/notification.service';
import { PagerService } from 'src/app/features/shared/pager/pager.service';
import { ProgressBarService } from 'src/app/features/shared/progress-bar/progress-bar.service';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { photoDropList } from 'src/app/features/shared/image-boxes/models/photo-drop';
import { _NavigationList_Base_FeatureComponent } from 'src/app/infrastructure/components/tablist-components/_navigation-list-base-feature.component';
import { _PageService } from 'src/app/infrastructure/services/page-services/_page.service';
import { routeActionParam } from 'src/app/infrastructure/services/page-services/models/return-page-query-param';
import { routePage } from 'src/app/infrastructure/services/route-service/models/route-page';
import { TabDataService } from 'src/app/features/shared/central-page/services/tab-data.service';
import { TabListService } from 'src/app/features/shared/central-page/services/tab-list.service';
import { TabListStorageService } from 'src/app/features/shared/storage/services/storage-services/tab-list-storage.service';
import { TPhotoName } from 'src/app/infrastructure/models/image-type';
import { claimName } from 'src/assets/common/enums/claim-name.g';
import { claimPage, ClaimPage } from 'src/app/infrastructure/models/claim-page';
import { httpParamKey } from 'src/app/infrastructure/services/request-services/models/http-param-name';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { loadKey } from 'src/app/infrastructure/services/loading-services/models/loadKey';
import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { tabName } from 'src/app/infrastructure/services/page-services/models/tab-name';
import { unwrapData } from 'src/app/infrastructure/functions/unwrap-data.operator';
import { NavigationList_ToolComponent } from 'src/app/features/shared/lists/navigation-list/container/navigation-list-tool/navigation-list.component';
import { TabList_ToolComponent } from 'src/app/features/shared/lists/tab-list/container/tab-list-tool/tab-list.component';
import { ILevelGeneralListData } from 'src/app/infrastructure/models/level-general-list-data';
import { ITabOwnerClaim } from 'src/app/features/shared/central-page/models/tab-data-template';
import { TranslocoService } from '@jsverse/transloco';
import { UploadLimitService } from '../../services/upload-limit.service';

// Maximaal aantal foto's per galerij-selectie/upload (camera maakt er sowieso 1 per keer).
const MAX_PHOTOS_PER_UPLOAD = 10;

// Scope (type + parent/route) waaraan de upload gekoppeld wordt. Eénmalig vastgelegd bij de start
// van een (multi-)upload, zodat een navigatie naar een ander object tijdens de batch de resterende
// foto's niet alsnog aan de verkeerde scope koppelt.
interface IUploadScope {
    imageType: string | undefined;
    guid: string | undefined;
    domainAUserGuid: string | undefined;
    projectAMemberGuid: string | undefined;
}

// Langdrukduur (ms) om de multi-select-modus te starten. Ruim langer dan de cdkDrag touch-delay
// (300 ms) zodat vasthouden+bewegen eerst 'slepen' wordt en alleen lang stilstaand-vasthouden
// 'select' triggert.
const LONG_PRESS_MS = 750;
// Bewegingsdrempel (px): hierboven is het slepen/scrollen, geen long-press.
const LONG_PRESS_MOVE_TOLERANCE = 10;

@Component({
    selector: 'app-image-tablist',
    templateUrl: './image-tablist.component.html',
    styleUrls: [
        '../../../shared/central-page/central-page.scss',
        '../../../shared/central-page/container/tablist/tablist.scss',
        './image-tablist.component.scss',
    ],
    standalone: true,
    imports: [
        ButtonActionComponent,
        CameraCaptureComponent,
        DragDropModule,
        ImageBoxTabListComponent,
        TabList_ToolComponent,
        SharedModule,
    ],
    providers: [
        { provide: LIST_KIND_NAME, useValue: tabName.images },
        TabListStorageService,
        TabDataService,
        PagerService,
        { provide: FilterService, useExisting: PagerService },

        ImageListService,
        { provide: _ListService, useExisting: ImageListService },
    ],
})
export class ImageTablistComponent
    extends _NavigationList_Base_FeatureComponent<IImageListDto, IImageListData>
    implements AfterViewInit
{
    private readonly __apiService = injectRequired(ApiService);
    private readonly __imageListService = injectRequired(ImageListService);
    private readonly __loadingPageService = injectRequired(LoadingPageService);
    private readonly __tabListService = injectRequired(TabListService);
    private readonly __fileResizeService = injectRequired(FileResizeService);
    private readonly __configService = injectRequired(ConfigService);
    private readonly __notificationService = injectRequired(NotificationService);
    private readonly __progressBarService = injectRequired(ProgressBarService);
    private readonly __transloco = injectRequired(TranslocoService);
    private readonly __uploadLimitService = injectRequired(UploadLimitService);
    // Voor emit_onDragMoved: CDK's pointer-tracking (dus ook cdkDragMoved) loopt bewust buiten
    // Angular's zone (performance) — zonder expliciete ngZone.run() daar zou een signal-write in die
    // handler geen render triggeren, zie de toelichting daar.
    private readonly __ngZone = injectRequired(NgZone);
    // De autorisatie-pagina (meervoud tab-page, bv. 'orderPhotos') komt van de wrapper via het
    // CLAIM_PAGE-token in diens providers — net als elke andere tablist die zijn pagina hard
    // declareert. Fallback op de [claimPageList]-input (nog niet-gemigreerde gebruikers) en daarna
    // 'images'. Voedt zowel de generieke knop-checks (_claimPageList$$, gezet in _ngOnInit) als de
    // eigen edit/add-checks.
    private readonly __claimPageToken = inject(CLAIM_PAGE, { optional: true });

    protected override readonly _listService = injectRequired(_ListService);
    // Foto-tabs draaien binnen een pagina die een _PageService levert (order/notice/...); optioneel
    // omdat de basis hem niet altijd nodig heeft. Navigatie naar de foto-detailpagina gebruikt hem.
    protected override readonly _pageService = inject(_PageService, { optional: true })!;
    protected override readonly _pagerService = injectRequired(PagerService);
    protected override readonly _tabDataService = injectRequired(TabDataService);
    protected override readonly _tabListService = injectRequired(TabListService);

    private readonly __filterPipe = new FilterPipe();

    photoType$$ = input<TPhotoName | null>(null, { alias: 'photoType' });
    // Enkelvoudige claim-page van de per-foto-pagina (image-details/edit): een echte ClaimPage
    // (pageName), losgekoppeld van photoType (FileType). Via de route meegegeven zodat de sectie
    // niet de FileType-discriminator als autorisatiepagina hoeft te misbruiken.
    claimPagePhoto$$ = input<ClaimPage | null>(null, { alias: 'claimPagePhoto' });

    // Twee zelfstandige checkbox-features, losstaand van het bestaande TabListService-gedreven
    // hoofdfoto-mechanisme hierboven (dat is gekoppeld aan de hoofdfoto-box van de container-pagina —
    // voor DomainUser's eigen tab is dat de verkeerde 'hoofdfoto', zie DomainUser.ProfilePhoto vs.
    // DomainFileBoxId). Beide lezen/schrijven per-item state (item.shared/item.isMain) rechtstreeks
    // via de PhotoController, alleen ingeschakeld door de wrapper die ze nodig heeft (DomainUserPhotos).
    shareToggle$$ = input<boolean>(false, { alias: 'shareToggle' });
    ownMainPhotoToggle$$ = input<boolean>(false, { alias: 'ownMainPhotoToggle' });

    // Schakelt het generieke TabListService-hoofdfoto-mechanisme uit (de knop hierboven-in-de-
    // toelichting, gekoppeld aan de hoofdfoto-box van de container-pagina) — voorheen impliciet al uit
    // zodra ownMainPhotoToggle aanstond (!ownMainPhotoToggle$$() in het template), maar DomainPhotos
    // (shareToggle zónder ownMainPhotoToggle, zie DomainUserPhotoTablistComponent) had anders zowel
    // geen ownMain- als geen generieke-suppressie: de generieke knop verscheen dan alsnog (foute
    // 'hoofdfoto' — dezelfde reden als de toelichting hierboven), vandaar los van ownMainPhotoToggle$$.
    // Default true: bestaande wrappers die dit niet opgeven blijven ongewijzigd.
    mainPhotoToggle$$ = input<boolean>(true, { alias: 'mainPhotoToggle' });

    // Alleen relevant in combinatie met ownMainPhotoToggle bij photoType userPhoto: false (default)
    // zet User.PhotoFileBoxId (Home, eigen hoofdfoto); true zet DomainUser.ProfileFileBoxId (de
    // domain-specifieke profielfoto-keuze, zie PhotoController.MainPhoto/AsProfilePhoto).
    asProfilePhoto$$ = input<boolean>(false, { alias: 'asProfilePhoto' });

    // Override van photoType$$ voor uitsluitend de ownMainPhoto-PATCH (__setOwnMainPhoto): nodig op de
    // geaggregeerde Domain-pagina Photos-tab, waar de LIJST met imageType domainPhoto opgehaald wordt
    // (alle gedeelde foto's van elke DomainUser), maar de eigen-hoofdfoto-keuze zelf met imageType
    // domainUserPhoto gezet moet worden (PhotoController.MainPhoto beperkt die case tot je EIGEN
    // uploads — anders zou domainPhoto daar de fallback-case hieronder raken). Valt terug op
    // photoType$$ zelf zodra dit leeg blijft (alle overige wrappers, ongewijzigd gedrag).
    ownMainImageType$$ = input<TPhotoName | null>(null, { alias: 'ownMainImageType' });

    // Afwijkende claim-page voor de eigen-hoofdfoto-knop (BtnMainPhotoEdit_Show, zie canOwnMainPhoto$$);
    // leeg = de claim-page van de lijst zelf (claimPageList$$).
    ownMainClaimPage$$ = input<ClaimPage | null>(null, { alias: 'ownMainClaimPage' });

    // Derde, zelfstandige checkbox-feature (checkbox-only, geen slepen): de domeinbrede fallback-
    // hoofdfoto (Domain.PhotoFileBoxId), voor wie BtnFallbackPhotoEdit_Show heeft op de DomainPhotos-tab — zie
    // PhotoController.MainPhoto, case DomainPhoto. Losstaand van ownMainPhotoToggle hierboven (die is
    // de PERSOONLIJKE keuze van elke DomainUser); beide kunnen tegelijk aan staan op dezelfde tab.
    fallbackPhotoToggle$$ = input<boolean>(false, { alias: 'fallbackPhotoToggle' });
    // imageType voor de fallback-PATCH (altijd domainPhoto in de praktijk, maar net als hierboven niet
    // hardcoded zodat deze generieke component type-onafhankelijk blijft).
    fallbackImageType$$ = input<TPhotoName | null>(null, { alias: 'fallbackImageType' });
    // Losse claim-page voor de fallback-knop/checkboxes (BtnFallbackPhotoEdit_Show erop vereist, zie
    // canFallbackPhoto$$) en geen owner-bypass — welke pagina dat is bepaalt de wrapper (in de
    // praktijk DomainPhotos, zie DomainPhotoTablistComponent), losstaand van claimPageList$$ gehouden
    // zodat een andere wrapper hier ooit een eigen pagina voor kan kiezen.
    fallbackClaimPage$$ = input<ClaimPage | null>(null, { alias: 'fallbackClaimPage' });

    // Eigenaarschap-bypass voor de footer-knoppen (toevoegen/edit-modus): alleen ingeschakeld door
    // wrappers die dit nodig hebben (DomainUserPhotos, EntityOwner) — bij de meeste andere foto-types
    // blijft dit null en verandert er dus niets. Los van de per-foto RecordOwner-check op de
    // detailpagina (image-details), die altijd al werkte.
    ownerClaim$$ = input<ITabOwnerClaim | null>(null, { alias: 'ownerClaim' });

    // Meldt de nieuwe eigen-hoofdfoto (of null bij verwijderen) na een geslaagde ownMain-PATCH — de
    // enige plek (bv. Home) waar deze persoonlijke keuze ook de getoonde hoofdfoto-box is, kan zo
    // zijn optimistische override zetten. Blijft ongebruikt bij DomainUserPhotos e.d. (zie de
    // decoupling-toelichting bij shareToggle$$/ownMainPhotoToggle$$ hierboven).
    ownMainPhotoChanged = output<IImageListData | null>();

    // Zelfde als ownMainPhotoChanged hierboven, maar voor de fallback-hoofdfoto (Domain.PhotoFileBoxId)
    // — de Domain-pagina's eigen banner toont precies dit veld (zie domain-data.ts), dus DomainComponent
    // gebruikt dit om die banner optimistisch bij te werken zonder een nieuwe route-resolve.
    fallbackPhotoChanged = output<IImageListData | null>();

    readonly loadKey = loadKey;

    override input_btnAddData$$ = this._input_btnAddData$$;
    override input_btnDeleteData$$ = this._input_btnDeleteData$$;
    override input_buttonsHide$$ = this._input_buttonsHide$$;
    override input_claimPageList$$ = this._input_claimPageList$$;
    override input_noClick$$ = this._input_noClick$$;
    override input_pageSize$$ = this._input_pageSize$$;

    override out_onClick = this._out_onClick;
    override out_onPageLoaded = this._out_onPageLoaded;
    override out_onSave = this._out_onSave;

    override btnAddData$$ = this._input_btnAddData$$;
    override btnDeleteData$$ = this._input_btnDeleteData$$;
    override buttonsHide$$ = this._input_buttonsHide$$;
    override noClick$$ = this._input_noClick$$;
    override pageSize$$ = this._input_pageSize$$;
    override httpParams$$ = this._computed_httpParams$$;
    override list$$ = this._computed_list$$;
    override requestName$$ = this._computed_requestName$$;
    override tabIndex$$ = this._computed_tabIndex$$;

    override claimPageList$$ = computed(() => this.__claimPageToken ?? this._input_claimPageList$$() ?? claimPage.images);

    override itemLines = 1;

    // Context waarin de selectie startte; wijzigt tab/filter ⇒ verlaat de selectiemodus.
    private __selectionContext: { tabIndex: number; filter: string | null } | null = null;
    // Long-press-bewaking.
    private __longPressTimer: ReturnType<typeof setTimeout> | null = null;
    private __longPressStart: { x: number; y: number } | null = null;
    private __longPressed = false;
    // Een drag is bezig: blokkeert het starten van de selectiemodus, zodat slepen en selecteren
    // elkaar nooit overlappen (regel: tijdens drag geen selectie).
    private __dragging = false;

    private readonly __grid$$ = viewChild<ElementRef<HTMLElement>>('grid');

    // FLIP-animatie: layout-posities van de tussenliggende sorteerstappen + de lopende animatie
    // per element (om te kunnen vervangen).
    private readonly __flipPositions = new Map<HTMLElement, { left: number; top: number }>();
    private readonly __flipAnimations = new Map<HTMLElement, Animation>();
    private __dragDurationMs = 250;

    ready$$ = signal(false);

    // Voor de footer-knoppen (template_buttons hieronder): alleen de PAGINA-brede load-status
    // (zelfde signaal als de andere tablist-tool-componenten), niet ready$$ — die reset bij elke
    // tab-wissel (Angular Material's mat-tab-body herbouwt de content standaard bij verlaten/
    // terugkeren, zie preserveContent). Op loadingPage$$ gaten zou de knoppen bij élke tab-wissel
    // laten verdwijnen/terugkomen i.p.v. alleen bij de initiële page-load — het laden ván de tab
    // zelf blijft zichtbaar via de bestaande spinner-overlay (!ready$$()) in de foto-grid.
    loadingPage$$ = this.__loadingPageService.isLoading$$;

    // Busy-overlay tijdens upload/delete: legt de grid op slot (blokkeert klikken) met een spinner,
    // zónder de content te verbergen. De voortgang zelf toont de footer-progressbar onderin.
    busy$$ = signal(false);

    // In-pagina camera-overlay (getUserMedia) i.p.v. de externe camera-app; voorkomt dat Chrome
    // de tab weggooit en de pagina herlaadt bij het maken van een foto (zie CameraCaptureComponent).
    cameraOpen$$ = signal(false);

    // De gefilterde, zichtbare lijst: de actieve foto's (niet-inactief) door de tab-filter. De grid
    // rendert deze; selectie/select-all/delete werken alleen op deze (zichtbare) items.
    protected readonly _filterString$$ = computed(() => this._pagerService.filterString$$());
    protected readonly _searchFields$$ = computed(() => this._tabDataService.searchFieldsActive$$());
    displayedList$$ = computed<IImageListData[]>(() => this.__filterPipe.transform(
        this._listService.listActive$$(),
        { filter: this._filterString$$(), fields: this._searchFields$$(), mode: this._pagerService.FilterMode },
    ));
    // Tijdens een actieve filter is de getoonde lijst een subset; reorderen zou de Nr-volgorde
    // corrumperen, dus slepen staat dan uit.
    protected readonly _filterActive$$ = computed(() => !!this._filterString$$());

    // Mainphoto-checkboxes staan standaard uit (verstoren anders de lijst) — pas zichtbaar via de
    // footer-toggle (zie template_buttons, alleen getoond bij edit-recht) en weer verborgen zodra je
    // ernaast klikt (zie emit_documentClick).
    mainPhotoCheckboxesVisible$$ = signal(false);

    // Delen (DomainUserPhotos, alleen beheerder), eigen-hoofdfoto-kiezen (DomainUserPhotos, iedere
    // DomainUser) en fallback-hoofdfoto-kiezen (Domain-pagina, wie BtnFallbackPhotoEdit_Show heeft op DomainPhotos) — zelfde
    // aan/uitklap-patroon als mainPhotoCheckboxesVisible$$ hierboven, maar wederzijds exclusief
    // daarmee én met elkaar (nooit twee checkbox-groepen tegelijk in dezelfde hoek).
    shareCheckboxesVisible$$ = signal(false);
    ownMainCheckboxesVisible$$ = signal(false);
    fallbackCheckboxesVisible$$ = signal(false);

    // 'Hoofdfoto kiezen' actief (beide varianten, zie hierboven): slepen binnen de grid herordent dan
    // niet meer (cdkDropListSortingDisabled) en emit_onDragMoved/emit_onDragEnded doen dan pas hun
    // eigen hittest tegen de hoofdfoto-box (zie daar voor waarom geen cdkDropListConnectedTo) —
    // anders wisselen normaal sorteren en slepen-naar-hoofdfoto elkaar af zodra je richting de box
    // sleept. Uit: gewoon sorteren, geen hoofdfoto-drop.
    protected readonly _mainPhotoDragActive$$ = computed(() => this.mainPhotoCheckboxesVisible$$() || this.ownMainCheckboxesVisible$$());

    // Delen mag alleen met de btnListShare_Show-claim (domeinbeheerder) én als de wrapper dit inschakelt.
    canShare$$ = computed(() => {
        if (!this.shareToggle$$()) return false;
        return this._applicationUserService.claim_check(
            this.claimPageList$$(), this._activatedRouteService.routeKeys, claimName.btnListShare_Show,
            this._applicationUserService.userClaims$$(),
        );
    });

    // Hoofdfoto kiezen in de lijst (checkbox + slepen naar de hoofdfoto-box): overal dezelfde regel —
    // claim BtnMainPhotoEdit_Show op de foto-pagina van deze lijst (<X>Photos), of via de owner-bypass
    // (ownerClaim$$). Zelfde generieke check als de banner-upload (_TabPageService.mainPhotoClaim_check,
    // daar op <X>Details), zodat "hoofdfoto kiezen in de lijst" en "uploaden in de banner" los
    // instelbaar zijn via de pagina-dimensie van de claim. Puur FE-zichtbaarheid: de backend checkt bij
    // /api/Photo/main zelf (MainPhoto_Update / Page_Update op de edit-pagina).
    // - canMainPhoto$$: de "echte" hoofdfoto van de container (Project/Contact/Notice), via photoDrop$.
    // - canOwnMainPhoto$$: de eigen keuze (Domain-herkenningsfoto, DomainUser-profielfoto, Home);
    //   ownMainClaimPage$$ alleen nodig als die afwijkt van de lijst-pagina.
    canMainPhoto$$ = computed(() => {
        if (!this.mainPhotoToggle$$() || this.ownMainPhotoToggle$$()) return false;
        return this._tabListService.mainPhotoClaim_check(this.claimPageList$$(), this.ownerClaim$$());
    });

    canOwnMainPhoto$$ = computed(() => {
        if (!this.ownMainPhotoToggle$$()) return false;
        return this._tabListService.mainPhotoClaim_check(this.ownMainClaimPage$$() ?? this.claimPageList$$(), this.ownerClaim$$());
    });

    // Fallback-hoofdfoto kiezen mag alleen met de claim BtnFallbackPhotoEdit_Show op fallbackClaimPage$$
    // (in de praktijk DomainPhotos, zie DomainPhotoTablistComponent) — geen owner-bypass, geen relatie
    // met canEdit$$ hierboven. Puur FE-zichtbaarheid: de backend dwingt bij /api/Photo/main zelf
    // Page_Update op DomainPhotos af (zelfde show/update-scheiding als BtnMainPhotoEdit_Show).
    canFallbackPhoto$$ = computed(() => {
        if (!this.fallbackPhotoToggle$$()) return false;
        const __page = this.fallbackClaimPage$$();
        if (!__page) return false;
        return this._applicationUserService.claim_check(
            __page, this._activatedRouteService.routeKeys, claimName.btnFallbackPhotoEdit_Show,
            this._applicationUserService.userClaims$$(),
        );
    });

    // ── Multi-select (long-press) ───────────────────────────────────────────
    selectionMode$$ = signal(false);
    selectedGuids$$ = signal<Set<string>>(new Set());

    // Selectie is gebonden aan de zichtbare items: de effectieve selectie = doorsnede met displayedList.
    selectedCount$$ = computed(() => {
        const __selected = this.selectedGuids$$();
        return this.displayedList$$().filter(item => __selected.has(item.guid)).length;
    });
    anySelected$$ = computed(() => this.selectedCount$$() > 0);
    allVisibleSelected$$ = computed(() => {
        const __visible = this.displayedList$$();
        return __visible.length > 0 && this.selectedCount$$() === __visible.length;
    });

    // Delete-recht hangt aan de enkelvoudige foto-pagina; die is identiek aan de photoType-waarde
    // (bv. 'orderPhoto') — zelfde page als de per-foto-editor (image-details). Bulk-delete gebruikt
    // dezelfde claim als de single delete.
    canDelete$$ = computed(() => {
        const __page = this.photoType$$() ?? claimPage.images;
        return this._applicationUserService.claim_check(
            __page, this._activatedRouteService.routeKeys, claimName.page_Delete,
            this._applicationUserService.userClaims$$(),
        );
    });

    canEdit$$ = computed(() => {
        if (this._applicationUserService.claim_check(
            this.claimPageList$$(), this._activatedRouteService.routeKeys, claimName.page_Update,
            this._applicationUserService.userClaims$$(),
        )) return true;

        const __owner = this.ownerClaim$$();
        return !!__owner && this._applicationUserService.ownerClaim_check(__owner.kind, __owner.ownerGuid, this.claimPageList$$(), claimName.page_Update);
    });

    // Toevoegen mag: claim btnListAdd_Show op de foto-pagina (de backend dwingt zelf page_Update af).
    canAdd$$ = computed(() => {
        if (this._applicationUserService.claim_check(
            this.claimPageList$$(), this._activatedRouteService.routeKeys, claimName.btnListAdd_Show,
            this._applicationUserService.userClaims$$(),
        )) return true;

        const __owner = this.ownerClaim$$();
        return !!__owner && this._applicationUserService.ownerClaim_check(__owner.kind, __owner.ownerGuid, this.claimPageList$$(), claimName.btnListAdd_Show);
    });

    // Maximum per lijst (FileType.MaxFiles, AdminPanel › configuration; alleen typen op gebruikersniveau, null =
    // onbeperkt): bij het maximum blijven camera/galerij zichtbaar maar disabled met de reden als tooltip
    // (recht wel, maar geblokkeerd). Telt de niet-verwijderde foto's van deze lijst, zoals de backend
    // (PhotoController.__UploadLimit_check).
    private readonly __maxFiles$$ = computed(() => this.__uploadLimitService.maxFiles_get(this.photoType$$()));
    private readonly __uploadRemaining$$ = computed<number | null>(() => {
        const __max = this.__maxFiles$$();
        if (__max == null) return null;
        const __count = (this.list$$() ?? []).filter(item => !item[imageListData.deleted]).length;
        return Math.max(0, __max - __count);
    });
    uploadLimitReached$$ = computed(() => this.__uploadRemaining$$() === 0);
    private readonly __uploadLimitHint$$ = computed(() =>
        this.__transloco.translate('hint.uploadLimit.reached', { max: this.__maxFiles$$() }));
    uploadCameraTooltip$$ = computed(() => this.uploadLimitReached$$() ? this.__uploadLimitHint$$() : 'foto maken');
    uploadGalleryTooltip$$ = computed(() => this.uploadLimitReached$$() ? this.__uploadLimitHint$$() : 'uit galerij kiezen');

    private readonly __templateButtons$$ = viewChild<TemplateRef<unknown>>('template_buttons');
    private readonly __galleryInput$$ = viewChild<ElementRef<HTMLInputElement>>('galleryInput');

    // 'image/*' (MIME-wildcard), niet een extensielijst (.jpg,.png,...): Android/Chrome toont de
    // native systeem-fotokiezer alleen bij een MIME-accept — met alleen extensies valt het terug op
    // een eigen, generieke bestandsbrowser i.p.v. de vertrouwde Foto's-picker. Serverzijde validatie
    // (ImageSharp) blijft de echte grens; dit is puur de UX-hint voor de bestandskiezer.
    protected readonly _acceptTypes$$ = computed<string>(() => 'image/*');

    // Drag start na long-press op touch (vasthouden ⇒ slepen, korte tik ⇒ openen, vegen ⇒
    // scrollen); op desktop direct slepen bij muisbeweging (klik ⇒ openen).
    readonly dragStartDelay = { touch: 300, mouse: 0 };

    // CDK drop-id van de grid zelf (eigen reorder). _mainDropId is GEEN cdkDropList-koppeling meer
    // (zie emit_onDragMoved/__isOverMainBox) — puur het DOM-id waarmee de hittest de hoofdfoto-box
    // van de container-pagina terugvindt (zie [[photoDropList]]).
    protected readonly _tablistDropId = photoDropList.tablist;
    protected readonly _mainDropId = photoDropList.main;

    constructor() {
        super();

        this.__uploadLimitService.load();

        // Header-knop/tab-wissel/filterwijziging verlaat de selectiemodus.
        effect(() => {
            const __tabIndex = this.tabIndex$$();
            const __filter = this._filterString$$();
            untracked(() => {
                if (!this.selectionMode$$()) return;
                const __ctx = this.__selectionContext;
                if (__ctx && (__ctx.tabIndex !== __tabIndex || __ctx.filter !== __filter)) {
                    this.emit_cancelSelection();
                }
            });
        });

        // Foto toegevoegd buiten deze tablist om (de hoofdfoto-box-upload, zie photoMainUpload$ in
        // de pagina-container): Angular Material vernietigt deze tab-inhoud niet zolang hij de
        // geselecteerde tab blijft, dus een needRefresh-vlag alleen wordt hier nooit meer gelezen.
        // Forceer daarom altijd meteen een verse fetch, ongeacht zichtbaarheid.
        this._tabListService.photoListChanged$
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.__listReload());

        // Sleep-naar-hoofdfoto voor de eigen-hoofdfoto-flow: emit_onDragEnded (eigen hittest, zie
        // daar) emit hierboven al generiek naar het gedeelde photoDrop$-pad van de container-pagina;
        // dat pad gebruikt normaal de container zelf (Project/Contact/Domain, "echte" hoofdfoto — zie
        // emit_mainPhotoDrop daar). Bij ownMainPhotoToggle is de eigen keuze juist de getoonde
        // hoofdfoto (bv. Home), dus vangen we de drop hier zelf af — alleen dan, zodat containers
        // zonder ownMainPhotoToggle (Project/Contact/Domain) ongewijzigd blijven. Container hoeft
        // alleen photoDropEnabledSource_set aan te sluiten om de box als hittest-doel te activeren.
        // Hoofdfoto-box is drop-target zolang déze lijst in hoofdfoto-modus staat (zie
        // _TabPageService.photoDropTarget_set) — afmelden bij destroy.
        effect(() => {
            const __active = this._mainPhotoDragActive$$();
            untracked(() => this._tabListService.photoDropTarget_set(this, __active));
        });
        inject(DestroyRef).onDestroy(() => this._tabListService.photoDropTarget_set(this, false));

        // De thumbnail-spinner (!ready$$) vult de nog lege grid: de footer-progressbar is dan
        // dubbelop, dus onderdrukken. Niet tijdens de page-load (inhoud verborgen, balk is dan de
        // enige feedback) — zelfde regel als de lijst-spinner in _SourceList_Base_ToolComponent.
        // De busy-overlay (upload/delete) telt bewust niet mee: daar ís de balk de voortgang.
        effect(() => {
            const __visible = !this.ready$$() && !this.loadingPage$$();
            untracked(() => this.__progressBarService.spinner_set(this, __visible));
        });
        inject(DestroyRef).onDestroy(() => this.__progressBarService.spinner_set(this, false));

        this._tabListService.photoDrop$
            .pipe(takeUntilDestroyed())
            .subscribe(item => {
                if (!this.ownMainPhotoToggle$$() || !this.canOwnMainPhoto$$()) return;
                this.__setOwnMainPhoto(item as IImageListData | null);
            });
    }

    override ngOnInit(): void {
        this._ngOnInit();
    }

    override emit_btnAddClick(): void {
        // Toevoegen verloopt via de camera/galerij-knoppen in de footer (zie template_buttons).
    }

    // Open de in-pagina camera-overlay (getUserMedia). Niet de externe camera-app aanroepen: die
    // verdringt Chrome uit het geheugen waardoor de tab herlaadt en de foto verloren gaat.
    emit_cameraClick(): void {
        this.cameraOpen$$.set(true);
    }

    // Foto gemaakt in de camera-overlay: overlay sluiten en via het gedeelde upload-pad verwerken.
    emit_cameraCaptured(file: File): void {
        this.cameraOpen$$.set(false);
        void this.__processFiles([file]);
    }

    emit_cameraClosed(): void {
        this.cameraOpen$$.set(false);
    }

    // Klik op het selectievakje van een tegel (togglet, los van navigatie/long-press).
    emit_checkboxClick(event: Event, item: IImageListData): void {
        event.stopPropagation();
        this.__toggleSelection(item);
    }

    // Klik op de mainphoto-checkbox: aanvinken zet deze foto als hoofdfoto (net als slepen naar de
    // imagebox), uitvinken van de huidige hoofdfoto verwijdert de hoofdfoto-koppeling. Loopt via
    // hetzelfde photoDrop$-pad als een echte CDK-drop (zie TabListService/[[container]].emit_mainPhotoDrop);
    // null betekent daar "verwijderen".
    emit_mainPhotoCheckboxClick(event: Event, item: IImageListData): void {
        event.stopPropagation();
        if (!this.canMainPhoto$$()) return;

        const __isCurrentMain = !!item.thumbGuid && item.thumbGuid === this._tabListService.photoGuid$$();
        this._tabListService.photoDrop_emit(__isCurrentMain ? null : item);

        this.__notificationService.pushNotification({
            severity: 'success',
            text: __isCurrentMain ? 'Hoofdfoto verwijderd' : 'Ingesteld als hoofdfoto',
        });
    }

    // Footer-toggle: mainphoto-checkboxes tonen/verbergen.
    emit_toggleMainPhotoCheckboxes(): void {
        this.mainPhotoCheckboxesVisible$$.update(visible => !visible);
    }

    // Footer-toggle: delen-checkboxes tonen/verbergen — sluit de andere checkbox-groepen uit (nooit
    // twee checkbox-groepen tegelijk rechtsboven).
    emit_toggleShareCheckboxes(): void {
        this.shareCheckboxesVisible$$.update(visible => !visible);
        this.ownMainCheckboxesVisible$$.set(false);
        this.fallbackCheckboxesVisible$$.set(false);
    }

    // Footer-toggle: eigen-hoofdfoto-checkboxes tonen/verbergen — sluit de andere groepen uit.
    emit_toggleOwnMainCheckboxes(): void {
        this.ownMainCheckboxesVisible$$.update(visible => !visible);
        this.shareCheckboxesVisible$$.set(false);
        this.fallbackCheckboxesVisible$$.set(false);
    }

    // Footer-toggle: fallback-hoofdfoto-checkboxes tonen/verbergen — sluit de andere groepen uit.
    emit_toggleFallbackCheckboxes(): void {
        this.fallbackCheckboxesVisible$$.update(visible => !visible);
        this.shareCheckboxesVisible$$.set(false);
        this.ownMainCheckboxesVisible$$.set(false);
    }

    // Klik op de delen-checkbox: togglet FileBox.Shared van de eigen foto direct (geen bevestiging) en
    // ververst de lijst zodat de bijgewerkte staat (en de Domain-pagina's geaggregeerde tab) klopt.
    // busy$$-guard (zie __setOwnMainPhoto/__setFallbackPhoto hieronder): zonder deze check kon een
    // dubbelklik dezelfde PATCH twee keer bijna gelijktijdig afvuren, waarbij de tweede tegen de
    // TimeStamp/rowversion van vóór de eerste save botste — DbUpdateConcurrencyException (0 rijen
    // geraakt) op de server, want DomainUser heeft een rowversion-kolom.
    emit_shareCheckboxClick(event: Event, item: IImageListData): void {
        event.stopPropagation();
        if (!this.canShare$$() || this.busy$$()) return;

        // domainAUserGuid ambient meesturen (zie __setOwnMainPhoto/__setFallbackPhoto hierboven):
        // zonder deze param kan de server ApplicationUser.DomainUser niet resolven en gooit
        // DomainUser_set "Gebruiker is geen lid van dit domain" (AppDomainUser_get).
        const __routeKeys = this._activatedRouteService.routeKeys;
        this.busy$$.set(true);
        from(this.__apiService.PATCH('/api/Photo/share', {
            params: { query: { domainAUserGuid: __routeKeys?.dauGuid ?? undefined } },
            body: { guid: item.guid, shared: !item.shared },
        })).pipe(
            finalize(() => this.busy$$.set(false)),
        ).subscribe(() => this.__listReload());
    }

    // Klik op de eigen-hoofdfoto-checkbox: zet (of verwijdert) DomainUser.DomainFileBoxId — de
    // persoonlijke keuze voor de herkenningsfoto van het Domain (fallback is Domain.PhotoFileBox).
    // busy$$-guard: zie toelichting bij emit_shareCheckboxClick hierboven (dubbelklik-race).
    emit_ownMainCheckboxClick(event: Event, item: IImageListData): void {
        event.stopPropagation();
        if (!this.canOwnMainPhoto$$() || this.busy$$()) return;

        this.__setOwnMainPhoto(item.isMain ? null : item);
    }

    // Gedeelde PATCH voor de eigen-hoofdfoto-keuze — aangeroepen vanuit zowel de checkbox-klik
    // hierboven als de sleep-naar-hoofdfoto-subscriptie in de constructor. ownMainImageType$$ valt
    // terug op photoType$$ zelf (zie de toelichting daar) zodat bestaande wrappers ongewijzigd blijven.
    // busy$$ blokkeert overlappende aanroepen (checkbox-dubbelklik én sleep-tijdens-checkbox-klik) —
    // zonder deze guard raceten twee bijna-gelijktijdige PATCHes tegen elkaar op DomainUser's
    // rowversion en gooide de tweede een DbUpdateConcurrencyException (500).
    private __setOwnMainPhoto(item: IImageListData | null): void {
        if (this.busy$$()) return;

        const __routeKeys = this._activatedRouteService.routeKeys;
        this.busy$$.set(true);
        from(this.__apiService.PATCH('/api/Photo/main', {
            params: { query: { domainAUserGuid: __routeKeys?.dauGuid ?? undefined } },
            body: { imageType: this.ownMainImageType$$() ?? this.photoType$$(), guid: item?.guid ?? null, asProfilePhoto: this.asProfilePhoto$$() },
        })).pipe(
            unwrapData<unknown>(),
            finalize(() => this.busy$$.set(false)),
        ).subscribe(() => {
            this.__listReload();
            this.ownMainPhotoChanged.emit(item);
            // Pas na bevestiging van de server (anders dan de container-route in emit_mainPhotoCheckboxClick).
            this.__notificationService.pushNotification({
                severity: 'success',
                text: item ? 'Ingesteld als hoofdfoto' : 'Hoofdfoto verwijderd',
            });
        });
    }

    // Klik op de fallback-hoofdfoto-checkbox: zet (of verwijdert) Domain.PhotoFileBoxId — de
    // domeinbrede fallback, voor wie BtnFallbackPhotoEdit_Show heeft op de DomainPhotos-tab (zie
    // PhotoController.MainPhoto, case DomainPhoto). Checkbox-only, geen slepen (zie
    // fallbackPhotoToggle$$-toelichting hierboven). busy$$-guard: zie emit_shareCheckboxClick hierboven.
    emit_fallbackCheckboxClick(event: Event, item: IImageListData): void {
        event.stopPropagation();
        if (!this.canFallbackPhoto$$() || this.busy$$()) return;

        this.__setFallbackPhoto(item.isFallback ? null : item);
    }

    private __setFallbackPhoto(item: IImageListData | null): void {
        const __routeKeys = this._activatedRouteService.routeKeys;
        this.busy$$.set(true);
        from(this.__apiService.PATCH('/api/Photo/main', {
            params: { query: { domainAUserGuid: __routeKeys?.dauGuid ?? undefined } },
            body: { imageType: this.fallbackImageType$$(), guid: item?.guid ?? null, asProfilePhoto: false },
        })).pipe(
            unwrapData<unknown>(),
            finalize(() => this.busy$$.set(false)),
        ).subscribe(() => {
            this.__listReload();
            this.fallbackPhotoChanged.emit(item);
            this.__notificationService.pushNotification({
                severity: 'success',
                text: item ? 'Ingesteld als standaard-hoofdfoto van het domain' : 'Standaard-hoofdfoto van het domain verwijderd',
            });
        });
    }

    emit_cancelSelection(): void {
        this.selectionMode$$.set(false);
        this.selectedGuids$$.set(new Set());
        this.__selectionContext = null;
    }

    // Verwijder de geselecteerde (zichtbare) foto's via het bestaande delete-deep-pad: de basis
    // toont de bevestiging, roept _deleteDeep_delete$ (override) aan en haalt ze uit de lijst.
    emit_deleteSelected(): void {
        const __selected = this.selectedGuids$$();
        const __items = this.displayedList$$().filter(item => __selected.has(item.guid));
        if (__items.length === 0) return;

        this._deleteDeep$(__items).subscribe({ complete: () => this.emit_cancelSelection() });
    }

    // Klik ergens op het scherm buiten een tegel/checkbox of een footer-actieknop: verlaat de
    // selectiemodus. Alleen de knoppen zelf (app-button-action) zijn uitgezonderd zodat toggle/delete
    // eerst hun werk doen — klikken op de lege footer-ruimte verlaat dus óók de modus.
    @HostListener('document:click', ['$event']) emit_documentClick(event: MouseEvent): void {
        const __target = event.target as HTMLElement;

        if (this.selectionMode$$() && !__target.closest('.thumbnail-item') && !__target.closest('app-button-action')) {
            this.emit_cancelSelection();
        }

        // Mainphoto-/delen-/eigen-hoofdfoto-/fallback-checkboxes verbergen zodra ernaast geklikt wordt
        // — alleen het vakje zelf (alle vier hergebruiken dezelfde .main-photo-checkbox-opmaak, nooit
        // tegelijk gerenderd) en de footer-knoppen houden ze open.
        if (!__target.closest('.main-photo-checkbox') && !__target.closest('app-button-action')) {
            if (this.mainPhotoCheckboxesVisible$$()) this.mainPhotoCheckboxesVisible$$.set(false);
            if (this.shareCheckboxesVisible$$()) this.shareCheckboxesVisible$$.set(false);
            if (this.ownMainCheckboxesVisible$$()) this.ownMainCheckboxesVisible$$.set(false);
            if (this.fallbackCheckboxesVisible$$()) this.fallbackCheckboxesVisible$$.set(false);
        }
    }

    // Gekozen bestand(en): elk verkleinen (config-grenzen) en sequentieel uploaden zodat de
    // keuzevolgorde (Nr) behouden blijft; lijst pas na afloop één keer verversen. De pagina is tijdens
    // de hele bewerking gelocked (loadingPage) zodat de gebruiker geen botsende acties kan starten en
    // de footer-progressbar de status toont; pas na de verse lijst-fetch wordt ontgrendeld.
    async emit_fileChange(event: Event): Promise<void> {
        const __input = event.target as HTMLInputElement;
        const __files = Array.from(__input.files ?? []);
        try {
            await this.__processFiles(__files);
        } finally {
            // Pas hier de input legen: op mobiel (iOS Safari / Android WebView) kan een vroege reset
            // de nog-niet-gelezen blob-backing van geselecteerde bestanden neutraliseren, waardoor een
            // deel van de batch leeg ge-upload wordt. Resetten ná verbruik houdt alle files geldig en
            // laat dezelfde selectie later opnieuw kiezen.
            __input.value = '';
        }
    }

    // Galerij openen — meerdere foto's tegelijk te kiezen (galleryInput heeft [multiple]).
    emit_galleryClick(): void {
        this.__pickerOpen(this.__galleryInput$$());
    }

    override emit_onClick(item: ILevelGeneralListData): void {
        const __item = item as IImageListData;
            
        // Net na een long-press hoort de afsluitende klik niet te navigeren/togglen.
        if (this.__longPressed) { this.__longPressed = false; return; }

        if (this.selectionMode$$()) {
            this.__toggleSelection(__item);
            return;
        }

        const __key = __item.originGuid ?? __item.thumbGuid;
        if (!__key) return;

        const __dauGuid = this._activatedRouteService.routeKeys?.dauGuid ?? undefined;
        const __pamGuid = this._activatedRouteService.routeKeys?.pamGuid ?? undefined;
        const __photoType = this.photoType$$();

        // Cache-bust-versie meegeven zodat image-details een ?v=-token op de afbeeldings-URL krijgt
        // (anders toont de browser na een annotatie het oude gecachte beeld bij heropenen).
        const __claimPagePhoto = this.claimPagePhoto$$();
        const __query: { [key: string]: string } = {};
        if (__photoType) __query['imageType'] = __photoType;
        if (__claimPagePhoto) __query['claimPage'] = __claimPagePhoto;
        if (__item.version) __query['v'] = __item.version;

        this._pageService?.page_goto(
            routePage.image,
            { key: __key, dauGuid: __dauGuid, pamGuid: __pamGuid },
            [routeActionParam.keep],
            Object.keys(__query).length ? __query : undefined,
        );
    }

    // Drag afgelopen (gedropt óf geannuleerd): de drag-vlag vrijgeven zodat selecteren weer mag, en —
    // in 'hoofdfoto kiezen'-modus — de eigen hittest tegen de hoofdfoto-box uitvoeren (zie
    // emit_onDragMoved hieronder voor waarom geen cdkDropListConnectedTo). De preview eerst wissen,
    // dan pas emitten: photoDrop_emit loopt synchroon door naar mainPhoto_change (optimistische
    // override), dus er zit geen zichtbaar gat tussen 'preview uit' en 'nieuwe foto in'.
    emit_onDragEnded(event: CdkDragEnd<IImageListData>, item: IImageListData): void {
        this.__dragging = false;
        this.__hoveringMainBox = false;

        const __wasOverMainBox = this._mainPhotoDragActive$$() && this.__isOverMainBox(event.dropPoint);
        this._tabListService.photoDragPreview_set(null);
        if (__wasOverMainBox) this._tabListService.photoDrop_emit(item);
    }

    // Tijdens het slepen: eigen pointer-vs-boundingrect-hittest tegen de hoofdfoto-box i.p.v. CDK's
    // cdkDropListConnectedTo — die deed een echte cross-list-transfer (de gesleepte tegel verhuisde
    // daadwerkelijk in de DOM naar de hoofdfoto-drop-zone zodra je erover hing), met twee glitches:
    // de tegel verdween tijdelijk uit de fotolijst, én de 'geldig-drop-doel'-rand van de hoofdfoto-box
    // doofde juist op dat moment (CDK's exit() op de bronlijst reset ook de 'receiving'-status van de
    // hoofdfoto-box zelf). Deze hittest raakt de grid-DOM niet aan: de tegel blijft altijd zichtbaar.
    //
    // cdkDragMoved (i.t.t. cdkDragStarted/cdkDragEnded/cdkDropListEntered/-Exited) loopt bewust
    // BUITEN Angular's zone (CDK's eigen performance-optimalisatie voor pointer-tracking, zie
    // DragRef._pointerDown/_pointerMove in @angular/cdk/drag-drop) — een signal-write hier alleen
    // zou dus geen render triggeren, en de rand/preview zouden nooit zichtbaar oplichten. __ngZone.run
    // haalt de write terug de zone in, alleen bij een ECHTE hover-overgang (niet bij elke
    // pointer-move) zodat dit geen volledige CD-tick per muisbeweging kost.
    private __hoveringMainBox = false;
    emit_onDragMoved(event: CdkDragMove<IImageListData>, item: IImageListData): void {
        if (!this._mainPhotoDragActive$$()) return;

        const __over = this.__isOverMainBox(event.pointerPosition);
        if (__over === this.__hoveringMainBox) return;
        this.__hoveringMainBox = __over;

        this.__ngZone.run(() => {
            this._tabListService.photoDragPreview_set(
                __over ? (item.thumbGuid ?? item.originGuid ?? item.guid) : null,
                item.version ?? null,
            );
        });
    }

    // Bounding-rect-hittest tegen de hoofdfoto-box (zie [[photoDropList]] voor het gedeelde id) —
    // null zodra deze pagina geen hoofdfoto-box met dat id rendert (bv. photoDropEnabled staat uit).
    private __isOverMainBox(point: { x: number; y: number }): boolean {
        const __rect = document.getElementById(this._mainDropId)?.getBoundingClientRect();
        return !!__rect && point.x >= __rect.left && point.x <= __rect.right && point.y >= __rect.top && point.y <= __rect.bottom;
    }

    // Leg de begin-posities vast zodat al de eerste sorteerstap soepel meeanimeert.
    emit_onDragStarted(): void {
        // Slepen begonnen: de long-press-timer afbreken én de drag-vlag zetten. CDK slokt tijdens het
        // slepen de pointermove-events op, dus emit_pointerMove kan de timer niet meer annuleren —
        // zonder dit zou de selectiemodus alsnog aanschieten midden in een drag (selectievakjes).
        this.__dragging = true;
        this.__hoveringMainBox = false;
        this.__longPressCancel();

        const __grid = this.__grid$$()?.nativeElement;
        if (!__grid) return;
        this.__dragDurationMs = this.__readDragDuration(__grid);
        this.__captureFlipPositions(__grid);
    }

    emit_onDrop(event: CdkDragDrop<IImageListData[]>): void {
        this.__flipReset();

        // In 'hoofdfoto kiezen'-modus is slepen uitsluitend bedoeld om naar de hoofdfoto-box te
        // verplaatsen (zie emit_onDragEnded/__isOverMainBox hierboven) — een drop terug in de grid
        // zelf is dan nooit een echte herordening (cdkDropListSortingDisabled voorkomt ook al de
        // live herorden-animatie in het template).
        if (this._mainPhotoDragActive$$()) return;

        const __list = [...this.displayedList$$()];
        moveItemInArray(__list, event.previousIndex, event.currentIndex);
        this.__imageListService.list_reorder(__list);

        const __photoType = this.photoType$$();
        if (!__photoType) return;

        const __items = __list.map((item, i) => ({ guid: item.guid, nr: i + 1 }));
        const __routeKeys = this._activatedRouteService.routeKeys;
        from(this.__apiService.PATCH('/api/Photo/sequence', {
            params: { query: {
                domainAUserGuid:    __routeKeys?.dauGuid ?? undefined,
                projectAMemberGuid: __routeKeys?.pamGuid ?? undefined,
            } },
            body: { imageType: __photoType, items: __items },
        })).subscribe();
    }

    override emit_onRestore(_restoreList: any[]): void {
        // Geen restore in de fotogrid (verwijderde foto's worden niet getoond).
    }

    // Elke sorteerstap (mixed-strategie herschikt de DOM rechtstreeks, zonder transform): meet de
    // nieuwe layout-posities en animeer het verschil met de vorige stap weg via FLIP.
    emit_onSorted(event: CdkDragSortEvent<IImageListData[]>): void {
        this.__flip(event.container.element.nativeElement);
    }

    // ── Long-press → selectiemodus ──────────────────────────────────────────
    emit_pointerDown(event: PointerEvent, item: IImageListData): void {
        this.__longPressed = false;
        if (this.selectionMode$$() || !this.canDelete$$()) return;

        this.__longPressStart = { x: event.clientX, y: event.clientY };
        this.__longPressTimer = setTimeout(() => {
            // Tijdens een actieve drag nooit de selectiemodus starten (drag en selectie gescheiden).
            if (this.__dragging) return;
            this.__longPressed = true;
            this.__enterSelection(item);
        }, LONG_PRESS_MS);
    }

    emit_pointerMove(event: PointerEvent): void {
        if (!this.__longPressStart) return;
        const __dx = Math.abs(event.clientX - this.__longPressStart.x);
        const __dy = Math.abs(event.clientY - this.__longPressStart.y);
        if (__dx > LONG_PRESS_MOVE_TOLERANCE || __dy > LONG_PRESS_MOVE_TOLERANCE) {
            this.__longPressCancel();
        }
    }

    emit_pointerUp(): void {
        this.__longPressCancel();
    }

    // Toggle: niets geselecteerd ⇒ alle zichtbare selecteren; anders selectie wissen.
    emit_toggleSelectAll(): void {
        if (this.anySelected$$()) {
            this.selectedGuids$$.set(new Set());
        } else {
            this.selectedGuids$$.set(new Set(this.displayedList$$().map(item => item.guid)));
        }
    }

    // Na het laden (basis-flow, finalize): preload de thumbnails en toon de grid. De centrale
    // loadingPage wordt direct afgemeld (super), de grid-overlay wacht op de thumbnails.
    protected override _emit_onPageLoaded(): void {
        this.__preloadThumbnails(this._listService.listActive$$());
        super._emit_onPageLoaded();
    }

    // Bulk definitief verwijderen: zelfde scope-params als list/sequence; de backend autoriseert op
    // de enkelvoudige fotopagina + Page_Delete en verwijdert deep.
    protected override _deleteDeep_delete$(deleteList: any[], _httpParams: HttpParams | null): Observable<IImageListDto> {
        const __routeKeys = this._activatedRouteService.routeKeys;
        // Alleen de FileBox-guids meesturen; volledige DTO's zouden op model-validatie (verplicht
        // FileBoxDto) stuiten en de backend resolvet de FileBoxen toch op Guid.
        const __guids = (deleteList as IImageListData[]).map(item => item.guid);

        // Busy-overlay (spinner + lock) + footer-progressbar (indeterminate) tijdens het verwijderen,
        // ná de bevestiging. De bevestigingsdialoog zelf blijft bedienbaar (busy gaat pas hier aan).
        this.busy$$.set(true);
        this.__progress_start(false);
        return from(this.__apiService.PATCH('/api/Photo/list-delete-deep', {
            params: { query: {
                imageType:          this.photoType$$() ?? undefined,
                domainAUserGuid:    __routeKeys?.dauGuid ?? undefined,
                projectAMemberGuid: __routeKeys?.pamGuid ?? undefined,
            } },
            body: __guids,
        })).pipe(
            unwrapData<IImageListDto>(),
            finalize(() => { this.busy$$.set(false); this.__progress_stop(); }),
        );
    }

    private __captureFlipPositions(grid: HTMLElement): void {
        this.__flipPositions.clear();
        for (const __el of this.__flipItems(grid)) {
            this.__flipPositions.set(__el, { left: __el.offsetLeft, top: __el.offsetTop });
        }
    }

    private __enterSelection(item: IImageListData): void {
        this.selectionMode$$.set(true);
        this.__selectionContext = { tabIndex: untracked(() => this.tabIndex$$()), filter: untracked(() => this._filterString$$()) };
        this.selectedGuids$$.set(new Set([item.guid]));
    }

    // De thumbnails die geanimeerd mogen worden: niet de placeholder en niet het gesleepte item.
    private __flipItems(grid: HTMLElement): HTMLElement[] {
        return Array.from(
            grid.querySelectorAll<HTMLElement>('.thumbnail-item:not(.cdk-drag-placeholder):not(.cdk-drag-dragging)'),
        ).filter(el => el.offsetParent !== null);
    }

    // FLIP: voor elk verschoven item het verschil (oude − nieuwe positie) als start-transform zetten
    // en naar 0 animeren, zodat de reflow er als een vloeiende verplaatsing uitziet.
    private __flip(grid: HTMLElement): void {
        for (const __el of this.__flipItems(grid)) {
            const __last = { left: __el.offsetLeft, top: __el.offsetTop };
            const __first = this.__flipPositions.get(__el);
            this.__flipPositions.set(__el, __last);
            if (!__first) continue;

            const __dx = __first.left - __last.left;
            const __dy = __first.top - __last.top;
            if (__dx === 0 && __dy === 0) continue;

            this.__flipAnimations.get(__el)?.cancel();
            // pointer-events uit tijdens de veeg: anders onderschept dit bewegende item CDK's
            // hit-test (elementFromPoint) onder de cursor en oscilleren de swaps → flikkering.
            __el.style.pointerEvents = 'none';
            const __anim = __el.animate(
                [{ transform: `translate(${__dx}px, ${__dy}px)` }, { transform: 'translate(0, 0)' }],
                { duration: this.__dragDurationMs, easing: 'cubic-bezier(0, 0, 0.2, 1)' },
            );
            __anim.onfinish = () => { __el.style.pointerEvents = ''; };
            this.__flipAnimations.set(__el, __anim);
        }
    }

    private __flipReset(): void {
        for (const __el of this.__flipAnimations.keys()) __el.style.pointerEvents = '';
        this.__flipPositions.clear();
        this.__flipAnimations.clear();
    }

    // httpParams van DEZE foto-lijst: eerst de eigen tab (TabDataService, per tablist-instantie), pas
    // daarna de actieve tab. Een pagina kan meerdere foto-tabs tegelijk levend hebben (bv. DomainUser:
    // Domain photos = DomainUser-guid, Profile photos = User-guid); photoListChanged$ ververst ze allemaal.
    // Met alleen de actieve tab kreeg een niet-actieve lijst de guid van een ándere tab mee (404
    // "DomainUser not found" na hoofdfoto instellen op Profile photos).
    private __ownHttpParams(): HttpParams | null {
        const __own = this._tabDataService.tabData$$()?.httpParams ?? null;
        if (__own) return __own;
        const __tabIndex = this._tabListService.tabIndex$$();
        return this._tabListService.tabList$$()[__tabIndex]?.httpParams ?? null;
    }

    protected override _list_get$(): Observable<IImageListDto[]> {
        const __httpParams = this.__ownHttpParams();
        const __guid = __httpParams?.get(httpParamKey.key) ?? undefined;
        const __imageType = this.photoType$$() ?? undefined;
        const __routeKeys = this._activatedRouteService.routeKeys;

        return from(this.__apiService.GET('/api/Photo/list', {
            params: { query: {
                imageType:          __imageType,
                guid:               __guid,
                domainAUserGuid:    __routeKeys?.dauGuid ?? undefined,
                projectAMemberGuid: __routeKeys?.pamGuid ?? undefined,
                asProfilePhoto:     this.asProfilePhoto$$(),
            } },
        })).pipe(map(r => (r.data as unknown as { list?: IImageListDto[] })?.list ?? []));
    }

    // Forceer een verse lijst-fetch (na upload) en haal de busy-overlay pas weg als de nieuwe lijst
    // binnen is — zo blijft de grid op slot tot de net geüploade foto's getoond kunnen worden (geen
    // flits van de oude lijst).
    private __listReload(): void {
        this._tabListService.tabList_setRefresh(true);
        this._listService.list_load$(() => this._list_get$()).pipe(
            catchError(() => EMPTY),
            finalize(() => { this.busy$$.set(false); this.__progress_stop(); }),
        ).subscribe();
    }

    private __longPressCancel(): void {
        if (this.__longPressTimer != null) {
            clearTimeout(this.__longPressTimer);
            this.__longPressTimer = null;
        }
        this.__longPressStart = null;
    }

    // Preload alle thumbnails (vullen de browsercache) en toon de grid pas als ze allemaal klaar
    // zijn, zodat de lijst in één keer verschijnt i.p.v. zich geleidelijk te vullen.
    private __preloadThumbnails(list: IImageListData[]): void {
        const __urls = list
            .map(item => item[imageListData.thumbGuid]
                ? `/api/Image/${item[imageListData.thumbGuid]}${item[imageListData.version] ? '?v=' + item[imageListData.version] : ''}`
                : null)
            .filter((u): u is string => u !== null);

        if (__urls.length === 0) { this.ready$$.set(true); return; }

        this.ready$$.set(false);
        let __remaining = __urls.length;
        const __done = () => { if (--__remaining === 0) this.ready$$.set(true); };

        for (const __url of __urls) {
            const __img = new Image();
            __img.onload = __done;
            __img.onerror = __done;
            __img.src = __url;
        }
    }

    // Verwerk de gekozen/gemaakte bestand(en): elk verkleinen (config-grenzen) en sequentieel
    // uploaden zodat de keuzevolgorde (Nr) behouden blijft; lijst pas na afloop één keer verversen. De
    // pagina is tijdens de hele bewerking gelocked (busy-overlay) zodat de gebruiker geen botsende
    // acties kan starten en de footer-progressbar de status toont; pas na de verse lijst-fetch wordt
    // ontgrendeld. Gedeeld door de galerij-input en de in-pagina camera.
    private async __processFiles(files: File[]): Promise<void> {
        if (files.length === 0) return;

        if (files.length > MAX_PHOTOS_PER_UPLOAD) {
            this.__notificationService.pushNotification({
                severity: 'warning',
                title: `Te veel foto's`,
                text: `Kies maximaal ${MAX_PHOTOS_PER_UPLOAD} foto's per keer.`,
            });
            return;
        }

        // Meer gekozen dan er nog bij mogen: niets uploaden (anders weigert de backend de rest één voor één).
        const __remaining = this.__uploadRemaining$$();
        if (__remaining != null && files.length > __remaining) {
            this.__notificationService.pushNotification({
                severity: 'warning',
                title: this.__transloco.translate('errors.uploadLimitReached.title'),
                text: this.__transloco.translate('hint.uploadLimit.remaining', { remaining: __remaining, max: this.__maxFiles$$() }),
            });
            return;
        }

        // Bij meerdere foto's determinate (loopt per voltooide foto op, "x van n"); bij één foto
        // indeterminate (een 0→100-sprong zou nietszeggend zijn).
        const __multi = files.length > 1;

        // Scope één keer vastleggen: tussentijds navigeren mag de resterende uploads niet verleggen.
        const __scope = this.__uploadScope_get();

        this.busy$$.set(true);
        this.__progress_start(__multi);
        try {
            const __resized: File[] = [];
            for (const __file of files) {
                try {
                    __resized.push(await this.__fileResizeService.resizeIfNeeded(
                        __file,
                        this.__configService.image_MaxWidth$$()  ?? 1920,
                        this.__configService.image_MaxHeight$$() ?? 1080,
                        this.__configService.image_MaxMB$$()     ?? 2,
                    ));
                } catch {
                    // Verkleinen mislukt (bv. een full-res camerafoto die de mobiele canvas-limiet
                    // raakt): upload het origineel ongewijzigd i.p.v. de hele batch af te breken; de
                    // backend maakt zelf de varianten/thumbnails.
                    __resized.push(__file);
                }
            }

            // Sequentieel uploaden (volgorde = Nr); een mislukte upload (centraal al gemeld) breekt
            // de rest van de batch niet af. Na elke foto de voortgang bijwerken.
            let __done = 0;
            for (const __file of __resized) {
                try { await firstValueFrom(this.__upload$(__file, __scope)); }
                catch { /* fout al centraal gerapporteerd */ }
                __done++;
                if (__multi) this.__progressBarService.ProgressValue = Math.round((__done / __resized.length) * 100);
            }
        } finally {
            // busy + progressbar blijven tot de verse lijst binnen is (afgehandeld in __listReload).
            this.__listReload();
        }
    }

    // Footer-progressbar starten/stoppen voor een taak (upload/delete), los van de page-loading.
    private __progress_start(determinate: boolean): void {
        this.__progressBarService.Mode = determinate ? 'determinate' : 'indeterminate';
        this.__progressBarService.ProgressValue = 0;
        this.__progressBarService.Active = true;
    }

    private __progress_stop(): void {
        this.__progressBarService.Active = false;
        this.__progressBarService.Mode = 'indeterminate';
        this.__progressBarService.ProgressValue = 0;
    }

    // Opent een verborgen bestand-input (camera of galerij); reset eerst de waarde zodat dezelfde
    // selectie opnieuw gekozen kan worden.
    private __pickerOpen(input: ElementRef<HTMLInputElement> | undefined): void {
        if (!input) return;
        input.nativeElement.value = '';
        input.nativeElement.click();
    }

    // Drag-animatieduur uit de theming-token (--layout-motion-duration-drag) in ms; valt terug op 250.
    private __readDragDuration(grid: HTMLElement): number {
        const __raw = getComputedStyle(grid).getPropertyValue('--layout-motion-duration-drag').trim();
        const __n = parseFloat(__raw);
        if (!Number.isFinite(__n)) return 250;
        return __raw.endsWith('ms') || !__raw.endsWith('s') ? __n : __n * 1000;
    }

    // Geen restore-pad nodig in de grid, maar de basis vereist een implementatie.
    protected override _restore_patch$(_restoreList: any[], _httpParams: HttpParams | null): Observable<IImageListDto> {
        return EMPTY;
    }

    private __toggleSelection(item: IImageListData): void {
        const __next = new Set(this.selectedGuids$$());
        if (__next.has(item.guid)) __next.delete(item.guid);
        else __next.add(item.guid);
        this.selectedGuids$$.set(__next);
    }

    // Upload één (verkleind) bestand als nieuwe foto naar de vastgelegde scope. De backend dwingt
    // het page_Update-recht af. Verversen van de lijst gebeurt door de aanroeper.
    private __upload$(file: File, scope: IUploadScope): Observable<unknown> {
        const __formData = new FormData();
        __formData.append('File', file, file.name);

        return from(this.__apiService.PATCH('/api/Photo/image-save', {
            params: { query: {
                imageType:          scope.imageType,
                guid:               scope.guid,
                domainAUserGuid:    scope.domainAUserGuid,
                projectAMemberGuid: scope.projectAMemberGuid,
            } },
            body: __formData as any,
        }));
    }

    // De upload-scope (type + parent/route) op dit moment vastleggen, zodat de hele batch aan dezelfde
    // scope gekoppeld blijft ook als de gebruiker tussendoor wegnavigeert.
    private __uploadScope_get(): IUploadScope {
        const __httpParams = this.__ownHttpParams();
        const __routeKeys = this._activatedRouteService.routeKeys;
        return {
            imageType:          this.photoType$$() ?? undefined,
            guid:               __httpParams?.get(httpParamKey.key) ?? undefined,
            domainAUserGuid:    __routeKeys?.dauGuid ?? undefined,
            projectAMemberGuid: __routeKeys?.pamGuid ?? undefined,
        };
    }

    // Registreer de footer-knoppen voor deze tab (zelfde mechanisme als de andere tablists).
    ngAfterViewInit(): void {
        setTimeout(() => {
            const __tabName = this._tabDataService.tabName$$();
            if (!__tabName) return;
            this.__tabListService.tabList_addContentButtons(__tabName, this.__templateButtons$$() ?? null);
        }, 0);
    }
}
